import React from 'react';
import styled from 'styled-components';
import Text from 'components/atoms/Text';
import Button from 'components/atoms/Button';
import { COLOR_SYSTEM } from 'components/design/design-system';

interface LicenseInfo {
  license: string;
  isRegistered: boolean;
  hasValidRole?: boolean;
  createdAt?: string;
}

interface LicenseGroup {
  role: string;
  licenseNumbers: LicenseInfo[];
  count: number;
}

interface PremiumLicenseListProps {
  /** Premium License 목록 (권한별로 그룹화) */
  licenses: LicenseGroup[];
  /** 삭제 버튼 클릭 핸들러 */
  onDeleteRole: (role: string, licenseNumbers: string[]) => void;
  /** 개별 면허번호 삭제 핸들러 */
  onDeleteLicense: (role: string, license: string) => void;
  /** 권한 부여 핸들러 */
  onGrantRole: (role: string, license: string) => void;
  /** 전체 다운로드 핸들러 */
  onExportAll: () => void;
  /** 지역별 다운로드 핸들러 */
  onExportRegion: (group: LicenseGroup) => void;
  /** 로딩 상태 */
  isLoading?: boolean;
}

const PremiumLicenseList: React.FC<PremiumLicenseListProps> = ({
  licenses,
  onDeleteRole,
  onDeleteLicense,
  onGrantRole,
  onExportAll,
  onExportRegion,
  isLoading = false,
}) => {
  if (!licenses || licenses.length === 0) {
    return (
      <EmptyContainer>
        <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[500]}>
          등록된 Premium License가 없습니다.
        </Text>
      </EmptyContainer>
    );
  }

  // 전체 면허번호 수 및 가입자 수 계산
  const totalCount = licenses.reduce((sum, item) => sum + item.count, 0);
  const registeredCount = licenses.reduce(
    (sum, item) => sum + item.licenseNumbers.filter((l) => l.isRegistered).length,
    0
  );
  const validRoleCount = licenses.reduce(
    (sum, item) => sum + item.licenseNumbers.filter((l) => l.isRegistered && l.hasValidRole).length,
    0
  );
  const needsRoleCount = licenses.reduce(
    (sum, item) => sum + item.licenseNumbers.filter((l) => l.isRegistered && l.hasValidRole === false).length,
    0
  );

  return (
    <ListContainer>
      <ListHeader>
        <TitleRow>
          <Text textType="H3" color={COLOR_SYSTEM.get('Gray')?.[900]}>
            등록된 Premium License 목록
          </Text>
          <Button
            size="Medium"
            type="IconWithText"
            variant="Outlined"
            color="Tertiary"
            icon="Download"
            left
            onClick={onExportAll}
            disabled={isLoading}
            disabledColor={isLoading}
          >
            전체 다운로드
          </Button>
        </TitleRow>
        <StatsRow>
          <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[600]}>
            총 {totalCount.toLocaleString()}개의 면허번호가 등록되어 있습니다.
          </Text>
          <StatsDivider>|</StatsDivider>
          <Text textType="Body2" color={COLOR_SYSTEM.get('Green')?.[600]}>
            정상 {validRoleCount.toLocaleString()}명
          </Text>
          {needsRoleCount > 0 && (
            <>
              <StatsDivider>|</StatsDivider>
              <Text textType="Body2" color={COLOR_SYSTEM.get('Orange')?.[600]}>
                권한부여필요 {needsRoleCount.toLocaleString()}명
              </Text>
            </>
          )}
          <StatsDivider>|</StatsDivider>
          <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[500]}>
            가입률 {totalCount > 0 ? Math.round((registeredCount / totalCount) * 100) : 0}%
          </Text>
        </StatsRow>
      </ListHeader>

      <RoleList>
        {licenses.map((item) => {
          const roleRegisteredCount = item.licenseNumbers.filter((l) => l.isRegistered).length;
          const roleValidCount = item.licenseNumbers.filter((l) => l.isRegistered && l.hasValidRole).length;
          const roleNeedsRoleCount = item.licenseNumbers.filter((l) => l.isRegistered && l.hasValidRole === false).length;

          return (
            <RoleCard key={item.role}>
              <RoleHeader>
                <RoleInfo>
                  <Text textType="H4" color={COLOR_SYSTEM.get('Gray')?.[900]}>
                    {item.role}
                  </Text>
                  <Badge>
                    <Text textType="Body2Medium" color={COLOR_SYSTEM.get('Blue')?.[600]}>
                      {item.count}개
                    </Text>
                  </Badge>
                  {roleValidCount > 0 && (
                    <RegisteredBadge>
                      <Text textType="Body2Medium" color={COLOR_SYSTEM.get('Green')?.[600]}>
                        정상 {roleValidCount}명
                      </Text>
                    </RegisteredBadge>
                  )}
                  {roleNeedsRoleCount > 0 && (
                    <WarningBadge>
                      <Text textType="Body2Medium" color={COLOR_SYSTEM.get('Orange')?.[700]}>
                        권한필요 {roleNeedsRoleCount}명
                      </Text>
                    </WarningBadge>
                  )}
                </RoleInfo>
                <RoleActions>
                  <Button
                    size="Medium"
                    type="IconWithText"
                    variant="Outlined"
                    color="Tertiary"
                    icon="Download"
                    left
                    onClick={() => onExportRegion(item)}
                    disabled={isLoading}
                    disabledColor={isLoading}
                  >
                    다운로드
                  </Button>
                  <Button
                    size="Medium"
                    type="IconWithText"
                    variant="Ghost"
                    color="Negative"
                    icon="Trash"
                    left
                    onClick={() => onDeleteRole(item.role, item.licenseNumbers.map(l => l.license))}
                    disabled={isLoading}
                  >
                    전체 삭제
                  </Button>
                </RoleActions>
              </RoleHeader>

            <LicenseNumberList>
              {item.licenseNumbers.map((licenseInfo, index) => (
                <LicenseNumberItem
                  key={`${licenseInfo.license}-${index}`}
                  $needsRole={licenseInfo.isRegistered && licenseInfo.hasValidRole === false}
                >
                  <LicenseContent>
                    {licenseInfo.isRegistered && licenseInfo.hasValidRole && (
                      <RegistrationIndicator
                        title="정상 (가입 + 권한 보유)"
                      />
                    )}
                    {licenseInfo.isRegistered && licenseInfo.hasValidRole === false && (
                      <WarningIndicatorWrapper
                        title="권한 부여 필요 (가입했으나 권한 없음) - 동그라미를 더블클릭하여 권한 부여"
                        onDoubleClick={(e: React.MouseEvent) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onGrantRole(item.role, licenseInfo.license);
                        }}
                      >
                        <WarningIndicator />
                      </WarningIndicatorWrapper>
                    )}
                    <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[700]}>
                      {licenseInfo.license}
                    </Text>
                  </LicenseContent>
                  <DeleteButton
                    className="delete-button"
                    size="Small"
                    type="Icon"
                    variant="Ghost"
                    color="Negative"
                    icon="Trash"
                    onClick={() => onDeleteLicense(item.role, licenseInfo.license)}
                    disabled={isLoading}
                  />
                </LicenseNumberItem>
              ))}
            </LicenseNumberList>
            </RoleCard>
          );
        })}
      </RoleList>
    </ListContainer>
  );
};

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const ListHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatsDivider = styled.span`
  color: ${COLOR_SYSTEM.get('Gray')?.[300]};
`;

const RoleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RoleCard = styled.div`
  border: 1px solid ${COLOR_SYSTEM.get('Gray')?.[200]};
  border-radius: 8px;
  padding: 20px;
  background-color: ${COLOR_SYSTEM.get('Gray')?.[0]};
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const RoleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get('Gray')?.[200]};
`;

const RoleInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RoleActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Badge = styled.div`
  padding: 4px 12px;
  border-radius: 12px;
  background-color: ${COLOR_SYSTEM.get('Blue')?.[50]};
`;

const RegisteredBadge = styled.div`
  padding: 4px 12px;
  border-radius: 12px;
  background-color: ${COLOR_SYSTEM.get('Green')?.[50]};
`;

const WarningBadge = styled.div`
  padding: 4px 12px;
  border-radius: 12px;
  background-color: ${COLOR_SYSTEM.get('Orange')?.[50]};
`;

const LicenseNumberList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
`;

const LicenseNumberItem = styled.div<{ $needsRole?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 4px;
  background-color: ${COLOR_SYSTEM.get('Gray')?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get('Gray')?.[200]};
  transition: all 0.2s;
  user-select: none;

  .delete-button {
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover {
    background-color: ${COLOR_SYSTEM.get('Gray')?.[100]};
    border-color: ${COLOR_SYSTEM.get('Gray')?.[300]};

    .delete-button {
      opacity: 1;
    }
  }

  ${(props) =>
    props.$needsRole &&
    `
    &:hover {
      background-color: ${COLOR_SYSTEM.get('Orange')?.[50]};
      border-color: ${COLOR_SYSTEM.get('Orange')?.[300]};
    }
  `}
`;

const LicenseContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RegistrationIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${COLOR_SYSTEM.get('Green')?.[500]};
  flex-shrink: 0;
`;

const WarningIndicatorWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  padding: 2px;
  margin: -2px;

  &:hover > div {
    transform: scale(1.3);
    background-color: ${COLOR_SYSTEM.get('Orange')?.[600]};
  }

  &:active > div {
    transform: scale(1.1);
  }
`;

const WarningIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${COLOR_SYSTEM.get('Orange')?.[500]};
  flex-shrink: 0;
  pointer-events: none;
  transition: all 0.2s;
`;

const DeleteButton = styled(Button)`
  min-width: auto;
  padding: 4px;
`;

const EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  border: 1px dashed ${COLOR_SYSTEM.get('Gray')?.[300]};
  border-radius: 8px;
  background-color: ${COLOR_SYSTEM.get('Gray')?.[50]};
`;

export default PremiumLicenseList;
